import { useState, useRef, useCallback } from 'react';
import { Camera, X, RotateCcw, Check } from 'lucide-react';

export default function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');

  const startCamera = useCallback(async () => {
    try {
      setError(null);
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStream(mediaStream);
    } catch (err) {
      setError('Nao foi possivel acessar a camera. Verifique as permissoes do navegador.');
    }
  }, [facingMode]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const takePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setPhoto(dataUrl);
    stopCamera();
  };

  const retake = () => {
    setPhoto(null);
    startCamera();
  };

  const confirm = () => {
    if (!photo) return;
    // Convert data URL to blob/file
    fetch(photo)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
        onCapture(file);
      });
  };

  const switchCamera = () => {
    stopCamera();
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    setTimeout(startCamera, 100);
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col" data-testid="camera-capture">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <h3 className="text-white font-display font-semibold">Captura por Camera</h3>
        <button onClick={handleClose} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors">
          <X size={20} />
        </button>
      </div>

      {/* Camera / Photo view */}
      <div className="flex-1 flex items-center justify-center relative overflow-hidden">
        {error && (
          <div className="text-center p-6">
            <p className="text-red-400 mb-4">{error}</p>
            <button onClick={startCamera} className="btn-primary">Tentar novamente</button>
          </div>
        )}

        {!error && !photo && !stream && (
          <div className="text-center">
            <Camera size={64} className="mx-auto text-white/30 mb-4" />
            <p className="text-white/60 mb-4">Posicione o documento de forma legivel</p>
            <button onClick={startCamera} className="btn-primary flex items-center gap-2 mx-auto" data-testid="start-camera-button">
              <Camera size={18} /> Iniciar Camera
            </button>
          </div>
        )}

        {stream && !photo && (
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="max-h-full max-w-full object-contain"
          />
        )}

        {photo && (
          <img src={photo} alt="Foto capturada" className="max-h-full max-w-full object-contain" />
        )}

        {/* Guide overlay */}
        {stream && !photo && (
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[60%] border-2 border-[var(--rn-green)] rounded-2xl opacity-50" />
            <p className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-sm text-center">
              Enquadre o documento dentro da area verde
            </p>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="p-4 flex items-center justify-center gap-4">
        {stream && !photo && (
          <>
            <button onClick={switchCamera} className="p-4 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
              <RotateCcw size={22} />
            </button>
            <button onClick={takePhoto} className="p-5 rounded-full bg-[var(--rn-green)] text-black hover:bg-[var(--rn-green-2)] transition-colors" data-testid="take-photo-button">
              <Camera size={28} />
            </button>
          </>
        )}
        {photo && (
          <>
            <button onClick={retake} className="btn-secondary flex items-center gap-2">
              <RotateCcw size={16} /> Tirar outra
            </button>
            <button onClick={confirm} className="btn-primary flex items-center gap-2" data-testid="confirm-photo-button">
              <Check size={16} /> Usar esta foto
            </button>
          </>
        )}
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
